import { ActivatedRoute, Router } from '@angular/router';
import {
  ChangeDetectorRef,
  Component,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ViewId, ViewPresetService } from '@services/view-preset.service';

import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { AppState } from 'src/app/reducers';
import { ColDef } from 'ag-grid-community';
import { CompanyService } from '../../services/company.service';
import { CoreService } from '../../services/core.service';
import { GeneralDynamicAgGridComponent } from 'src/app/ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { AgDateRangeFilterComponent } from 'src/app/ag-grid/components/ag-date-range-filter/ag-date-range-filter.component';

@Component({
  selector: 'app-dimensions',
  templateUrl:
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.html',
  styleUrls: [
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DimensionsComponent extends GeneralDynamicAgGridComponent {
  public viewId: ViewId = ViewId.Dimensions;
  tableTitle = this.translateService.instant('grid.title.dimensions');
  tableIcon: IconProp = ['fas', 'layer-group'];

  protected generalGridClassName: string = 'dimensions-grid';

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected store: Store<AppState>,
    protected route: ActivatedRoute,
    protected cdr: ChangeDetectorRef,
    protected router: Router,
    protected companyService: CompanyService,
    protected translateService: TranslateService,
    protected logger: LoggerService,
    protected viewPresetService: ViewPresetService,
    protected coreService: CoreService,
    protected editService: ObjectEditorService,
    protected toastService: ToastService,
  ) {
    super(
      store,
      route,
      cdr,
      logger,
      router,
      viewPresetService,
      toastService,
      translateService,
    );
  }

  protected fetchAgGridData(request: any): Observable<any> {
    return this.coreService.fetchUserDimensionsAgGrid(request);
  }

  createColumnDefs(): ColDef[] {
    return [
      {
        headerName: 'Name',
        field: 'title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'title',
          objectId: 'object_id',
          objectClassId: 'object_class_id',
          objectTypeId: 'object_type_id',
          lifecycleStatus: 'lifecycle_status',
          onClick: (data) => {
            this.editService.loadFeedAndOpenPopup(data.id);
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: 'Description',
        field: 'description',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: this.translateService.instant('grid.column.count-used'),
        field: 'count_used_as_dimension',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
      },
      {
        headerName: this.translateService.instant('grid.column.workspace'),
        field: 'workspace.title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'workspace.title',
          icon: ['fas', 'folder'],
          objectId: 'workspace.id',
          objectClassId: 'workspace.object_class_id',
          lifecycleStatus: 'workspace.lifecycle_status',
          onClick: (data) => {
            if (data.can_view && data.can_edit) {
              const url = this.router.serializeUrl(
                this.router.createUrlTree([
                  environment.baseUrlEnvironmentSuffix,
                  'analytics',
                  'workspace',
                  data.workspace.id,
                ]),
              );
              window.open(url, '_blank');
            } else {
              this.toastService.showInfo(
                this.translateService.instant(
                  'alerts.error.you-do-not-have-permissions-or-object-not-active',
                ),
                this.translateService.instant('dimensions.error.not-available'),
              );
            }
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.owner'),
        field: 'owner',
        filter: 'agTextColumnFilter',
        maxWidth: 200,
        sortable: true,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'owner.name',
          avatarKey: 'owner.avatar_url',
          objectId: 'owner.id',
          objectClassId: 'owner.object_class_id',
          lifecycleStatus: 'owner.lifecycle_status',
          onClick: (data) => {
            this.router.navigate(['admin', 'home', data.user.id]);
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.time_created'),
        field: 'time_created.absolute',
        filter: AgDateRangeFilterComponent,
        maxWidth: 180,
        sortable: true,
        cellRenderer: AgDateCellRendererComponent,
      },
      {
        headerName: this.translateService.instant('grid.column.time_updated'),
        field: 'time_updated.absolute',
        filter: AgDateRangeFilterComponent,
        maxWidth: 180,
        sortable: true,
        cellRenderer: AgDateCellRendererComponent,
      },
    ];
  }
}
